import React, { useState, useEffect } from 'react';
import { User, Building2, Briefcase, Mail, Phone, ExternalLink, UserPlus } from 'lucide-react';
import { lookupContactByEmail } from '../../services/emailService';

const SenderInfo = ({ emailAddress, suggestedContactName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!emailAddress) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      try {
        const result = await lookupContactByEmail(emailAddress);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [emailAddress]);

  if (loading) return null;

  // Unknown sender — show "Add as contact" prompt if we have a suggestion
  if (!data?.found) {
    if (!suggestedContactName) return null;

    // Extract clean email
    let cleanEmail = emailAddress || '';
    if (cleanEmail.includes('<')) cleanEmail = cleanEmail.split('<')[1]?.split('>')[0] || cleanEmail;

    // Extract name from "Name (Role)" format
    const parts = suggestedContactName.match(/^(.+?)\s*\((.+)\)$/);
    const name = parts ? parts[1].trim() : suggestedContactName;
    const role = parts ? parts[2] : null;

    return (
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg overflow-hidden">
        <a href={`/contacts?action=create&email=${encodeURIComponent(cleanEmail)}&name=${encodeURIComponent(name)}`}
          className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/15 transition-colors">
          <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-4 h-4 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-200">Unknown sender</span>
              {role && (
                <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[10px] font-medium">{role}</span>
              )}
            </div>
            <span className="text-xs text-blue-400/70 block truncate">
              Add {name || cleanEmail} as a contact
            </span>
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
        </a>
      </div>
    );
  }

  const { contact, cases, email_count } = data;

  const statusColors = {
    active: 'bg-green-500/20 text-green-300',
    former: 'bg-gray-500/20 text-gray-300',
    prospective: 'bg-blue-500/20 text-blue-300',
  };

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-700/30 transition-colors text-left">
        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
          {contact.entity_type === 'Organization'
            ? <Building2 className="w-4 h-4 text-cyan-400" />
            : <User className="w-4 h-4 text-cyan-400" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{contact.display_name}</span>
            {contact.is_client && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[contact.client_status] || statusColors.active}`}>
                {contact.client_status || 'Client'}
              </span>
            )}
          </div>
          {contact.company && (
            <span className="text-xs text-slate-400 truncate block">{contact.company}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {email_count > 0 && (
            <span className="text-xs text-slate-500">{email_count} emails</span>
          )}
          <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-slate-700/30 space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            {contact.email && (
              <div className="flex items-center gap-1.5 text-slate-400">
                <Mail className="w-3 h-3" /> {contact.email}
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-1.5 text-slate-400">
                <Phone className="w-3 h-3" /> {contact.phone}
              </div>
            )}
          </div>

          {cases.length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Cases</span>
              <div className="mt-1 space-y-1">
                {cases.map(c => (
                  <a key={c.id} href={`/cases/${c.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 bg-slate-700/30 rounded hover:bg-slate-700/50 transition-colors group">
                    <Briefcase className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-400" />
                    <span className="text-xs text-slate-300 truncate flex-1">{c.display_number || c.case_number} — {c.case_name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      c.status === 'Active' ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                    }`}>{c.status}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {contact.notes && (
            <p className="text-xs text-slate-500 italic truncate">Note: {contact.notes}</p>
          )}

          <a href={`/contacts/${contact.id}`}
            className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors">
            View full contact <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
};

export default SenderInfo;
